import { Button, DatePicker, Form, Input, Space } from 'antd';
import moment from 'moment';
import React from 'react';
import { errorWithUserMessage } from '../../../helpers/NotificationHelper';
import { ApiException, ApiTokensClient, CreateApiTokenRequest } from '../../../utils/api';
import { Props } from './index';

interface State {
    isSaving: boolean;
}

class ApiTokenForm extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            isSaving: false,
        };
    }

    // Disallow choosing a date in the past, or more than 10 years out
    // (a longer JWT lifetime breaks token issuance).
    private disabledDate = (current: moment.Moment): boolean => {
        if (!current) {
            return false;
        }
        const tooEarly = current < moment().startOf('day');
        const tooLate = current > moment().add(10, 'years').endOf('day');
        return tooEarly || tooLate;
    };

    private onSubmit = async (values: any): Promise<void> => {
        const { onCreated } = this.props;

        this.setState({ isSaving: true });

        try {
            const client = new ApiTokensClient();
            const request = new CreateApiTokenRequest({
                name: values.name,
                // Expire at the end of the chosen day.
                expiresAt: (values.expiresAt as moment.Moment).endOf('day').toDate(),
            });

            const result = await client.create(request);
            onCreated(result);
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error creating token.');
            }
        } finally {
            this.setState({ isSaving: false });
        }
    };

    public render(): React.ReactElement {
        const { onCancel } = this.props;
        const { isSaving } = this.state;

        return (
            <Form layout="vertical" onFinish={this.onSubmit}>
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                        { required: true, message: 'Name is required!' },
                        { max: 200, message: 'Too long input!' },
                    ]}
                >
                    <Input placeholder="e.g. Stats server (production)" />
                </Form.Item>

                <Form.Item label="Role">
                    {/* Tokens are always issued with the Reporter role (read access to all data). */}
                    <Input value="Reporter" disabled />
                </Form.Item>

                <Form.Item
                    name="expiresAt"
                    label="Expires"
                    initialValue={moment().add(1, 'year')}
                    rules={[{ required: true, message: 'Expiry is required!' }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                        disabledDate={this.disabledDate}
                        allowClear={false}
                    />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={isSaving}>
                            Create token
                        </Button>
                        <Button onClick={onCancel} disabled={isSaving}>
                            Cancel
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        );
    }
}

export default ApiTokenForm;
